<template>
  <div class="admin-page">
    <div class="admin-page-inner">
      <div class="admin-page-header">
        <div>
          <span class="admin-kicker">Abrechnung</span>
          <h1 class="admin-page-title">Rechnungsprüfung</h1>
          <p class="admin-page-subtitle">
            Abrechnungsvorschau für Subunternehmer auf Basis der Ist-Zeiten
          </p>
        </div>
      </div>

      <div v-if="loadingSubunternehmer" class="flex items-center justify-center py-20">
        <div class="text-center">
          <div class="inline-block animate-spin rounded-full h-12 w-12 border-4 border-gray-300 border-t-caspyellow mb-4"></div>
          <p class="text-gray-600 font-medium">Lade Subunternehmer...</p>
        </div>
      </div>

      <div v-else class="space-y-6">
        <!-- Auswahl: Subunternehmer + Zeitraum -->
        <div class="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
          <h3 class="text-2xl font-bold text-gray-800 mb-4">Subunternehmer und Zeitraum auswählen</h3>
          <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-2">Subunternehmer</label>
              <select v-model="selectedKontaktId" @change="onSelectionChanged"
                class="w-full border-2 border-gray-300 rounded-xl px-4 py-3 focus:border-caspyellow focus:ring-2 focus:ring-caspyellow focus:ring-opacity-20 focus:outline-none transition-all duration-200">
                <option :value="null">Bitte wählen...</option>
                <option v-for="sub in subunternehmer" :key="sub.id" :value="sub.id">
                  {{ sub.unternehmensname }}<template v-if="sub.firmenkuerzel"> ({{ sub.firmenkuerzel }})</template>
                </option>
              </select>
            </div>
            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-2">Von Datum</label>
              <input type="date" v-model="von" @change="onSelectionChanged"
                class="w-full border-2 border-gray-300 rounded-xl px-4 py-3 focus:border-caspyellow focus:ring-2 focus:ring-caspyellow focus:ring-opacity-20 focus:outline-none transition-all duration-200" />
            </div>
            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-2">Bis Datum</label>
              <input type="date" v-model="bis" @change="onSelectionChanged"
                class="w-full border-2 border-gray-300 rounded-xl px-4 py-3 focus:border-caspyellow focus:ring-2 focus:ring-caspyellow focus:ring-opacity-20 focus:outline-none transition-all duration-200" />
            </div>
          </div>
        </div>

        <!-- Vertragsauswahl -->
        <div v-if="loadingVertraege" class="bg-white rounded-2xl shadow-lg p-6 border border-gray-200 text-center">
          <div class="inline-block animate-spin rounded-full h-8 w-8 border-4 border-gray-300 border-t-caspyellow mb-2"></div>
          <p class="text-gray-600 font-medium">Lade Verträge...</p>
        </div>

        <div v-else-if="vertraegeLoaded && contracts.length === 0"
          class="bg-white rounded-2xl shadow-lg p-6 border border-gray-200 text-center">
          <p class="text-gray-500 font-medium">Keine Aufträge des Subunternehmers im gewählten Zeitraum gefunden.</p>
        </div>

        <div v-else-if="contracts.length > 0"
          class="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-200">
          <div class="px-6 py-3" style="background-color: #5f656e;">
            <div class="flex justify-between items-center">
              <h3 class="text-lg font-bold text-white">Verträge im Zeitraum</h3>
              <div class="flex gap-2">
                <button type="button" @click="selectAllContracts"
                  class="admin-button--secondary admin-button--sm">
                  Alle auswählen
                </button>
                <button type="button" @click="deselectAllContracts"
                  class="admin-button--secondary admin-button--sm">
                  Alle abwählen
                </button>
              </div>
            </div>
          </div>
          <div class="p-3 grid grid-cols-1 md:grid-cols-2 gap-1 max-h-96 overflow-y-auto">
            <label v-for="contract in contracts" :key="contract.vertragsbeschreibung"
              class="flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-pointer text-sm transition-colors duration-150"
              :class="contract.selected ? 'bg-yellow-50' : 'hover:bg-gray-50 opacity-70'">
              <input type="checkbox" v-model="contract.selected" @change="onContractToggled" class="w-4 h-4 shrink-0" />
              <span class="flex-1 min-w-0 truncate font-medium text-gray-800">
                {{ contract.vertragsbeschreibung }}
              </span>
              <span v-if="contract.hasOverride"
                class="shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold bg-purple-100 text-purple-800">
                Abweichend
              </span>
              <span v-if="!contract.vertragId"
                class="shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold bg-gray-200 text-gray-600">
                Kein Vertrag
              </span>
              <span class="shrink-0 text-xs text-gray-500 whitespace-nowrap">
                {{ contract.workedJobCount }} Aufträge · {{ formatHours(contract.totalHours) }} h
              </span>
            </label>
          </div>
        </div>

        <!-- Live-Vorschau -->
        <div v-if="previewLoading" class="bg-white rounded-2xl shadow-lg p-6 border border-gray-200 text-center">
          <div class="inline-block animate-spin rounded-full h-8 w-8 border-4 border-gray-300 border-t-caspyellow mb-2"></div>
          <p class="text-gray-600 font-medium">Berechne Vorschau...</p>
        </div>

        <template v-else-if="preview">
          <div class="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
            <div class="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
              <div>
                <h3 class="text-2xl font-bold text-gray-800">Voraussichtliche Abrechnung</h3>
                <p class="text-sm text-gray-500 mt-1">
                  {{ preview.subunternehmer.unternehmensname }} · {{ formatDate(preview.zeitraum.von) }} – {{ formatDate(preview.zeitraum.bis) }}
                </p>
              </div>
              <div class="text-right">
                <p class="text-sm font-semibold text-gray-500">Gesamt</p>
                <p class="text-3xl font-bold text-gray-900">{{ formatCurrency(preview.totalAmount) }}</p>
                <p class="text-sm text-gray-500">{{ formatHours(preview.totalHours) }} h · {{ preview.totalJobs }} Aufträge</p>
              </div>
            </div>
            <div v-if="preview.totalJobsWithoutTarif > 0"
              class="mt-4 p-3 bg-orange-50 border border-orange-200 rounded-xl text-sm text-orange-800">
              <i class="fa fa-triangle-exclamation mr-1"></i>
              {{ preview.totalJobsWithoutTarif }} Aufträge ohne passenden Tarif (weder Basistarif noch vertragsspezifischer Tarif gültig) – diese wurden nicht berücksichtigt.
            </div>
          </div>

          <div v-for="contract in preview.contracts" :key="contract.vertragsbeschreibung"
            class="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-200">
            <div class="px-6 py-4" style="background-color: #5f656e;">
              <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                <h3 class="text-xl font-bold text-white">{{ contract.vertragsbeschreibung }}</h3>
                <div class="flex flex-wrap gap-2">
                  <span v-for="tarif in contract.appliedTarife" :key="tarif.id"
                    class="inline-flex items-center px-3 py-1 rounded-lg text-xs font-semibold"
                    :class="tarif.isOverride ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'">
                    {{ tarif.isOverride ? 'Abweichend: ' : 'Basistarif: ' }}{{ tarif.name }}
                  </span>
                  <span v-if="contract.appliedTarife.length === 0"
                    class="inline-flex items-center px-3 py-1 rounded-lg text-xs font-semibold bg-red-100 text-red-800">
                    Kein Tarif angewendet
                  </span>
                </div>
              </div>
            </div>
            <div class="p-6">
              <table v-if="contract.lineItems.length > 0" class="w-full text-sm">
                <thead>
                  <tr class="text-left text-gray-500 border-b border-gray-200">
                    <th class="pb-2 font-semibold">Position</th>
                    <th class="pb-2 font-semibold text-right">Stunden</th>
                    <th class="pb-2 font-semibold text-right">Satz</th>
                    <th class="pb-2 font-semibold text-right">Betrag</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in contract.lineItems" :key="item.description" class="border-b border-gray-100">
                    <td class="py-2" :class="{ 'pl-6 text-gray-600': item.isSurcharge }">{{ item.description }}</td>
                    <td class="py-2 text-right">{{ formatHours(item.hours) }} h</td>
                    <td class="py-2 text-right">{{ formatCurrency(item.rate) }}</td>
                    <td class="py-2 text-right font-semibold">{{ formatCurrency(item.amount) }}</td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr class="border-t-2 border-gray-300">
                    <td class="pt-2 font-bold text-gray-800">Zwischensumme</td>
                    <td class="pt-2 text-right font-bold">{{ formatHours(contract.totalHours) }} h</td>
                    <td></td>
                    <td class="pt-2 text-right font-bold text-gray-900">{{ formatCurrency(contract.totalAmount) }}</td>
                  </tr>
                </tfoot>
              </table>
              <p v-else class="text-sm text-gray-500">Keine abrechenbaren Positionen.</p>
              <div v-for="warning in contract.warnings" :key="warning.type + (warning.category || warning.zlKey || '')"
                class="mt-3 text-sm text-orange-700">
                <i class="fa fa-triangle-exclamation mr-1"></i>
                <template v-if="warning.type === 'missing_times'">
                  {{ warning.count }} Aufträge ohne Ist-Zeiten wurden übersprungen.
                </template>
                <template v-else-if="warning.type === 'no_tarif'">
                  {{ warning.count }} Aufträge ohne gültigen Tarif wurden übersprungen.
                  <template v-if="warning.baseTarife && warning.baseTarife.length">
                    Zugeordnete Basistarife:
                    <span v-for="(tarif, i) in warning.baseTarife" :key="tarif.name">
                      {{ i > 0 ? ', ' : '' }}„{{ tarif.name }}" ({{ formatDate(tarif.laufzeitVon) || 'offen' }} – {{ formatDate(tarif.laufzeitBis) || 'offen' }})
                    </span>
                  </template>
                </template>
                <template v-else-if="warning.type === 'missing_category'">
                  {{ warning.count }} Aufträge: Kein Preis für Kategorie "{{ warning.category }}" im Tarif.
                </template>
                <template v-else-if="warning.type === 'missing_surcharge'">
                  {{ formatHours(warning.hours) }} h {{ warning.label }} in {{ warning.count }} Aufträgen ohne Satz "{{ warning.zlKey }}" im Tarif – nicht berechnet.
                </template>
              </div>
              <p class="mt-2 text-xs text-gray-500">
                {{ contract.billedJobCount }} von {{ contract.jobCount }} Aufträgen berücksichtigt
              </p>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import CASP from '../../CASP';

export default {
  name: 'AdminRechnungspruefung',
  data() {
    return {
      loadingSubunternehmer: true,
      subunternehmer: [],
      selectedKontaktId: null,
      von: '',
      bis: '',
      loadingVertraege: false,
      vertraegeLoaded: false,
      contracts: [],
      preview: null,
      previewLoading: false,
      vertraegeRequestId: 0,
      previewRequestId: 0
    };
  },
  methods: {
    onSelectionChanged() {
      this.contracts = [];
      this.vertraegeLoaded = false;
      this.preview = null;
      if (!this.selectedKontaktId || !this.von || !this.bis) return;
      this.loadVertraege();
    },
    loadVertraege() {
      const requestId = ++this.vertraegeRequestId;
      this.loadingVertraege = true;
      CASP.getRechnungspruefungVertraege(this.selectedKontaktId, this.von, this.bis, (status, response) => {
        if (requestId !== this.vertraegeRequestId) return;
        this.loadingVertraege = false;
        if (status === 200) {
          this.contracts = response.map(contract => ({ ...contract, selected: true }));
          this.vertraegeLoaded = true;
          this.loadPreview();
        } else {
          this.vertraegeLoaded = true;
          alert('Fehler beim Laden der Verträge: ' + (response?.error || response));
        }
      });
    },
    onContractToggled() {
      this.loadPreview();
    },
    selectAllContracts() {
      this.contracts.forEach(contract => { contract.selected = true; });
      this.loadPreview();
    },
    deselectAllContracts() {
      this.contracts.forEach(contract => { contract.selected = false; });
      this.loadPreview();
    },
    loadPreview() {
      const selected = this.contracts
        .filter(contract => contract.selected)
        .map(contract => contract.vertragsbeschreibung);
      if (!this.selectedKontaktId || selected.length === 0) {
        this.preview = null;
        this.previewLoading = false;
        this.previewRequestId++;
        return;
      }
      const requestId = ++this.previewRequestId;
      this.previewLoading = true;
      CASP.previewRechnungspruefung(this.selectedKontaktId, this.von, this.bis, selected, (status, response) => {
        if (requestId !== this.previewRequestId) return;
        this.previewLoading = false;
        if (status === 200) {
          this.preview = response;
        } else {
          this.preview = null;
          alert('Fehler beim Laden der Vorschau: ' + (response?.error || response));
        }
      });
    },
    formatCurrency(amount) {
      return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount || 0);
    },
    formatHours(hours) {
      return new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(hours || 0);
    },
    formatDate(dateString) {
      if (!dateString) return '';
      return new Date(dateString).toLocaleDateString('de-DE');
    },
    initDefaultZeitraum() {
      // Standard: letzter Kalendermonat
      const now = new Date();
      const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastOfPrevious = new Date(firstOfMonth.getTime() - 1);
      const firstOfPrevious = new Date(lastOfPrevious.getFullYear(), lastOfPrevious.getMonth(), 1);
      this.von = this.toDateInputValue(firstOfPrevious);
      this.bis = this.toDateInputValue(lastOfPrevious);
    },
    toDateInputValue(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
  },
  mounted() {
    this.initDefaultZeitraum();
    CASP.getRechnungspruefungSubunternehmer((status, response) => {
      this.loadingSubunternehmer = false;
      if (status === 200) {
        this.subunternehmer = response;
      } else {
        alert('Fehler beim Laden der Subunternehmer: ' + (response?.error || response));
      }
    });
  },
  created() {
    document.getElementById("mainspinner").style.display = "none";
  }
};
</script>
